import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiLogOut, FiMapPin, FiDollarSign, FiPackage, FiTruck, FiNavigation, FiToggleLeft, FiToggleRight, FiRefreshCw, FiCreditCard, FiBell, FiWifi, FiFileText, FiAlertCircle, FiCheck, FiShoppingBag } from 'react-icons/fi';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency, formatDistance } from '@/utils/formatters';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import MPConfigCard from '@/components/common/MPConfigCard';
import wsService from '@/services/websocket';
import { NotificationToggleCompact } from '@/components/common/NotificationToggle';

const EntregadorDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout, updateLocation, updateOnlineStatus } = useAuth();
  const [stats, setStats] = useState(null);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState(user?.location || null);
  const [isOnline, setIsOnline] = useState(user?.status_online || false);
  const [activeTab, setActiveTab] = useState('available');
  const [showPaymentConfig, setShowPaymentConfig] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isWsConnected, setIsWsConnected] = useState(false);

  // WebSocket connection for real-time notifications
  useEffect(() => {
    if (!token) return;
    
    wsService.connect(token);
    
    // Check connection status
    const checkConnection = setInterval(() => {
      setIsWsConnected(wsService.ws?.readyState === WebSocket.OPEN);
    }, 1000);
    
    // Listen for new delivery notifications
    const handleNewDelivery = (payload) => {
      console.log('New delivery available:', payload);
      setNotification({
        type: 'new_delivery',
        message: payload.message || '🚀 Nova entrega disponível!',
        distance: payload.distance_km
      });
      
      // Play notification sound
      playNotificationSound();
      
      // Reload available orders
      loadData();
      
      // Clear notification after 10 seconds
      setTimeout(() => setNotification(null), 10000);
    };
    
    wsService.addListener('new_delivery_available', handleNewDelivery);
    
    return () => {
      clearInterval(checkConnection);
      wsService.removeListener('new_delivery_available', handleNewDelivery);
    };
  }, [token]);

  const playNotificationSound = () => {
    try {
      // Create a simple beep sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
        audioContext.close();
      }, 200);
    } catch (e) {
      console.log('Could not play sound');
    }
  };

  useEffect(() => {
    getLocation();
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const newLocation = { lat: position.coords.latitude, lng: position.coords.longitude };
          setLocation(newLocation);
          try { await updateLocation(newLocation); } catch (err) { console.log('Location update skipped'); }
        },
        (e) => {
          console.error('Geolocation error:', e);
          // Fallback to Barueri
          setLocation({ lat: -23.5114, lng: -46.8763 });
        },
        { enableHighAccuracy: true }
      );
    }
  };

  const loadData = async () => {
    try {
      const [statsRes, availRes, myRes] = await Promise.all([
        api.get('/stats/delivery'),
        api.get('/orders/available').catch(() => ({ data: [] })),
        api.get('/orders')
      ]);
      setStats(statsRes.data);
      setAvailableOrders(availRes.data);
      setMyOrders(myRes.data.filter(o => o.status !== 'entregue'));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleToggleOnline = async () => {
    try {
      const newStatus = !isOnline;
      await updateOnlineStatus(newStatus);
      setIsOnline(newStatus);
    } catch (e) { console.error(e); }
  };

  const handleAcceptOrder = async (orderId) => {
    try {
      await api.post(`/orders/${orderId}/accept`);
      setNotification({
        type: 'success',
        message: '✅ Entrega aceita! Vá até a loja.'
      });
      setTimeout(() => setNotification(null), 3000);
      loadData();
      navigate(`/entregador/entrega/${orderId}`);
    } catch (e) {
      alert(e.response?.data?.detail || 'Erro ao aceitar entrega');
    }
  };

  const calculateDistance = (lat, lng) => {
    if (!location || !lat || !lng) return null;
    const R = 6371;
    const dLat = (lat - location.lat) * Math.PI / 180;
    const dLng = (lng - location.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) ** 2 + Math.cos(location.lat * Math.PI / 180) * Math.cos(lat * Math.PI / 180) * Math.sin(dLng/2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><LoadingSpinner /></div>;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Notification Banner */}
      {notification && (
        <div className={`fixed top-0 left-0 right-0 z-50 p-4 ${
          notification.type === 'new_delivery' ? 'bg-[#DC143C] animate-pulse' : 'bg-green-500'
        } text-white text-center font-semibold shadow-lg`}>
          <div className="flex items-center justify-center gap-2">
            <FiBell className="animate-bounce" />
            <span>{notification.message}</span>
            {notification.distance && <span className="text-sm opacity-80">({notification.distance}km)</span>}
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-gradient-to-r from-[#1A1A1A] to-[#DC143C] text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                <span className="font-bold text-sm md:text-lg text-white">BARUERI</span>
                <svg width="22" height="28" viewBox="0 0 80 100" className="md:w-7 md:h-9" style={{filter: 'drop-shadow(0 0 2px rgba(255, 215, 0, 0.5))'}}>
                  <defs>
                    <linearGradient id="boltGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#FFED4A" />
                      <stop offset="50%" stopColor="#FFD700" />
                      <stop offset="100%" stopColor="#FFA500" />
                    </linearGradient>
                  </defs>
                  <path d="M45 0 L25 40 L40 40 L20 70 L35 70 L10 100 L50 60 L35 60 L55 30 L40 30 L60 0 Z" fill="url(#boltGrad2)"/>
                </svg>
                <span className="font-bold text-sm md:text-lg text-[#FFD700]">FLASH</span>
              </div>
              <span className="text-xs md:text-sm bg-white/20 px-2 py-0.5 rounded hidden sm:inline">Entregador</span>
            </div>
            <div className="flex items-center gap-1 md:gap-2">
              {/* Connection Status */}
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] md:text-xs ${
                isWsConnected ? 'bg-green-500/30' : 'bg-yellow-500/30'
              }`}>
                <FiWifi className={isWsConnected ? '' : 'animate-pulse'} size={12} />
                <span className="hidden sm:inline">{isWsConnected ? 'Online' : '...'}</span>
              </div>
              {/* Push Notifications Toggle */}
              <NotificationToggleCompact className="text-white" />
              {/* Link para Compras */}
              <button 
                onClick={() => navigate('/vitrine')} 
                className="p-2 hover:bg-white/10 rounded-full"
                title="Fazer Compras"
              >
                <FiShoppingBag className="text-lg md:text-xl" />
              </button>
              <button onClick={() => { logout(); navigate('/'); }} className="p-2 hover:bg-white/10 rounded-full" data-testid="entregador-logout-btn">
                <FiLogOut className="text-lg md:text-xl" />
              </button>
            </div>
          </div>

          {/* Online Toggle */}
          <div className="flex items-center justify-between bg-white/10 rounded-xl p-2 md:p-3">
            <div className="flex items-center gap-2 md:gap-3">
              <button onClick={handleToggleOnline} className={`p-1.5 md:p-2 rounded-full transition-all ${isOnline ? 'bg-green-500' : 'bg-gray-500'}`}>
                {isOnline ? <FiToggleRight className="text-lg md:text-xl" /> : <FiToggleLeft className="text-lg md:text-xl" />}
              </button>
              <span className="font-medium text-sm md:text-base">{isOnline ? '🟢 Online' : '⚫ Offline'}</span>
            </div>
            {location && (
              <div className="flex items-center gap-1 text-xs md:text-sm text-white/70">
                <FiMapPin size={14} /> <span className="hidden sm:inline">Barueri, SP</span>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 md:px-4 py-4 md:py-6 space-y-3 md:space-y-4">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-2 md:gap-4">
            <div className="bg-white rounded-xl p-3 md:p-4 shadow-sm text-center">
              <FiTruck className="mx-auto text-xl md:text-2xl text-[#DC143C] mb-1" />
              <p className="text-lg md:text-2xl font-bold">{stats.total_deliveries}</p>
              <p className="text-[10px] md:text-xs text-gray-500">Entregas</p>
            </div>
            <div className="bg-white rounded-xl p-3 md:p-4 shadow-sm text-center">
              <FiDollarSign className="mx-auto text-xl md:text-2xl text-green-600 mb-1" />
              <p className="text-lg md:text-2xl font-bold">{formatCurrency(stats.total_earnings)}</p>
              <p className="text-[10px] md:text-xs text-gray-500">Ganhos</p>
            </div>
            <div className="bg-white rounded-xl p-3 md:p-4 shadow-sm text-center">
              <FiPackage className="mx-auto text-xl md:text-2xl text-orange-600 mb-1" />
              <p className="text-lg md:text-2xl font-bold">{stats.active_orders}</p>
              <p className="text-[10px] md:text-xs text-gray-500">Ativos</p>
            </div>
          </div>
        )}

        {/* Payment Config Button */}
        <button
          onClick={() => setShowPaymentConfig(!showPaymentConfig)}
          className={`w-full py-3 px-4 rounded-xl shadow-sm flex items-center justify-center gap-2 transition-colors ${
            stats?.mp_configured 
              ? 'bg-green-50 text-green-700 border border-green-200' 
              : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
          }`}
        >
          <FiCreditCard />
          {stats?.mp_configured ? 'Configuração de Pagamentos ✓' : '⚠️ Configure seu Mercado Pago'}
        </button>

        {/* Payment Config Panel */}
        {showPaymentConfig && (
          <MPConfigCard userType="entregador" />
        )}

        {/* Documents Status Card */}
        <Link
          to="/entregador/documentos"
          className={`w-full py-3 px-4 rounded-xl shadow-sm flex items-center justify-between transition-colors ${
            user?.documents_verified 
              ? 'bg-green-50 text-green-700 border border-green-200' 
              : user?.documents_status === 'pending'
                ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          <div className="flex items-center gap-2">
            <FiFileText />
            <span>
              {user?.documents_verified 
                ? 'Documentos Verificados ✓' 
                : user?.documents_status === 'pending'
                  ? '⏳ Documentos em Análise'
                  : '⚠️ Envie seus Documentos'}
            </span>
          </div>
          {user?.documents_verified ? (
            <FiCheck className="text-green-600" />
          ) : (
            <FiAlertCircle className={user?.documents_status === 'pending' ? 'text-yellow-600' : 'text-red-600'} />
          )}
        </Link>

        {/* Earnings Link */}
        <Link
          to="/entregador/ganhos"
          className="w-full py-3 px-4 rounded-xl shadow-sm flex items-center justify-between bg-gradient-to-r from-green-50 to-green-100 text-green-700 border border-green-200"
        >
          <div className="flex items-center gap-2">
            <FiDollarSign />
            <span>Histórico de Ganhos</span>
          </div>
          <span className="font-bold">{formatCurrency(stats?.wallet_balance || 0)}</span>
        </Link>

        {/* Express Services Link (Leva e Traz / Ponto a Ponto) */}
        <Link
          to="/entregador/express"
          className="w-full py-3 px-4 rounded-xl shadow-sm flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700 border border-blue-200"
        >
          <div className="flex items-center gap-2">
            <FiRefreshCw />
            <span>Pedidos Express (Leva e Traz)</span>
          </div>
          <span className="text-sm bg-blue-100 px-2 py-1 rounded-full">Novo!</span>
        </Link>

        {/* Real-time notification info */}
        {isOnline && (
          <div className="bg-[#DC143C]/10 border border-[#DC143C]/30 rounded-xl p-3 flex items-center gap-3">
            <FiBell className="text-[#DC143C] text-xl" />
            <div>
              <p className="font-medium text-[#DC143C]">Notificações Ativas</p>
              <p className="text-sm text-gray-600">Você receberá alertas de novas entregas em tempo real</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2">
          <button 
            onClick={() => setActiveTab('available')} 
            className={`flex-1 py-3 rounded-xl font-medium transition-all ${activeTab === 'available' ? 'bg-[#DC143C] text-white' : 'bg-white text-gray-600'}`}
          >
            Disponíveis ({availableOrders.length})
          </button>
          <button 
            onClick={() => setActiveTab('my')} 
            className={`flex-1 py-3 rounded-xl font-medium transition-all ${activeTab === 'my' ? 'bg-[#DC143C] text-white' : 'bg-white text-gray-600'}`}
          >
            Minhas ({myOrders.length})
          </button>
        </div>

        {/* Orders List */}
        {activeTab === 'available' ? (
          availableOrders.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center shadow-sm">
              <FiPackage className="mx-auto text-4xl text-gray-300 mb-3" />
              <p className="text-gray-500">Nenhum pedido disponível na sua região</p>
              <p className="text-sm text-gray-400 mt-1">Fique online para receber notificações</p>
            </div>
          ) : (
            <div className="space-y-4">
              {availableOrders.map((order) => {
                const distance = calculateDistance(order.delivery_coordinates?.lat, order.delivery_coordinates?.lng);
                return (
                  <div key={order.id} className="bg-white border-2 border-transparent hover:border-[#DC143C] rounded-xl p-4 shadow-sm transition-all">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-[#1A1A1A]">{order.store_name}</h3>
                        <p className="text-sm text-gray-500">{order.items.length} itens</p>
                      </div>
                      <span className="text-xl font-bold text-green-600">{formatCurrency(order.driver_amount)}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1"><FiMapPin className="text-[#DC143C]" /><span className="truncate">{order.delivery_address}</span></div>
                      {distance && <div className="flex items-center gap-1"><FiNavigation className="text-[#DC143C]" /><span>{formatDistance(distance)}</span></div>}
                    </div>
                    <button 
                      onClick={() => handleAcceptOrder(order.id)} 
                      className="w-full py-3 bg-[#DC143C] text-white rounded-xl font-semibold hover:bg-[#B01030] transition-colors"
                    >
                      ⚡ Aceitar Entrega
                    </button>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          myOrders.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center shadow-sm">
              <FiTruck className="mx-auto text-4xl text-gray-300 mb-3" />
              <p className="text-gray-500">Você não tem entregas ativas</p>
            </div>
          ) : (
            <div className="space-y-4">
              {myOrders.map((order) => (
                <div key={order.id} onClick={() => navigate(`/entregador/entrega/${order.id}`)} className="bg-white rounded-xl p-4 shadow-sm cursor-pointer hover:shadow-md transition-all border-l-4 border-[#DC143C]">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold">{order.store_name}</h3>
                      <p className="text-sm text-[#DC143C] font-medium">{order.status === 'coleta' ? '📦 Colete na loja' : '🚚 Em rota'}</p>
                    </div>
                    <span className="text-lg font-bold text-green-600">{formatCurrency(order.driver_amount)}</span>
                  </div>
                  <p className="text-sm text-gray-600 truncate">{order.delivery_address}</p>
                </div>
              ))}
            </div>
          )
        )}

        {/* Refresh Button */}
        <button 
          onClick={loadData} 
          className="w-full py-3 bg-white text-gray-600 rounded-xl font-medium flex items-center justify-center gap-2 shadow-sm hover:bg-gray-50"
        >
          <FiRefreshCw /> Atualizar
        </button>
      </main>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white/60 py-4 text-center text-sm mt-8">
        <p>© 2025 BarueriFlash - Entregador ⚡</p>
      </footer>
    </div>
  );
};

export default EntregadorDashboard;
